import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import MenuBar from "@/components/MenuBar";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { ArrowLeft, Check, X, Trash2, Edit, Copy, Mail } from "lucide-react";
import { AMAService } from "@/services/amaService";
import { formatDistanceToNow } from "date-fns";
import { nb } from "date-fns/locale";
import { AMAQuestion } from "@/types/ama";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { baseEmailConfig } from "@/utils/emailConfig";

const AMAModeration = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const queryClient = useQueryClient();

  const { data: ama } = useQuery({
    queryKey: ['ama', id],
    queryFn: () => AMAService.getAMAById(id!),
  });

  const { data: questions, isLoading } = useQuery({
    queryKey: ['ama-questions', id],
    queryFn: () => AMAService.getQuestionsByAMA(id!),
  });

  const { data: answers } = useQuery({
    queryKey: ['ama-answers', id],
    queryFn: () => AMAService.getAnswersByAMA(id!),
  });

  const updateStatusMutation = useMutation({
    mutationFn: ({ questionId, status }: { questionId: string; status: 'approved' | 'rejected' }) =>
      AMAService.updateQuestionStatus(questionId, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ama-questions', id] });
      toast.success(t('ama.moderation.updateSuccess'));
    },
    onError: () => {
      toast.error(t('ama.moderation.updateError'));
    },
  });

  const deleteQuestionMutation = useMutation({
    mutationFn: (questionId: string) => AMAService.deleteQuestion(questionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ama-questions', id] });
      toast.success(t('ama.moderation.deleteSuccess'));
    },
    onError: () => {
      toast.error(t('ama.moderation.deleteError'));
    },
  });

  const getStatusBadge = (status: string) => {
    const variants = {
      pending: "secondary",
      approved: "default",
      rejected: "destructive",
    } as const;

    return (
      <Badge variant={variants[status as keyof typeof variants]}>
        {t(`ama.questionStatus.${status}`)}
      </Badge>
    );
  };

  const hasAnswer = (questionId: string) => {
    return answers?.some(a => a.question_id === questionId);
  };

  const expertLink = ama?.expert_token
    ? `${baseEmailConfig.baseUrl}/ama/answer/${ama.expert_token}`
    : '';

  const copyLink = () => {
    if (expertLink) {
      navigator.clipboard.writeText(expertLink);
      toast.success(t('ama.form.linkCopied'));
    }
  };

  const sendInvitation = () => {
    if (!ama) return;
    
    const subject = encodeURIComponent(`Invitasjon til å delta i AMA: ${ama.topic}`);
    const body = encodeURIComponent(
      `Hei,\n\nDu er invitert til å delta i en AMA (Ask Me Anything) sesjon om "${ama.topic}".\n\nBruk følgende lenke for å svare på spørsmål:\n${expertLink}\n\nVennlig hilsen,\nRedaksjonen`
    );
    
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  if (isLoading) {
    return <div className="p-6">{t('common.loading')}</div>;
  }

  return (
    <div className="min-h-screen bg-background">
      <MenuBar />
      
      <div className="container mx-auto p-6 space-y-6">
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          onClick={() => navigate('/editorial/ama')}
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          {t('common.back')}
        </Button>
        <Button
          variant="outline"
          onClick={() => navigate(`/editorial/ama/${id}/edit`)}
        >
          <Edit className="mr-2 h-4 w-4" />
          {t('ama.moderation.editAMA')}
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{ama?.topic}</CardTitle>
          <p className="text-sm text-muted-foreground">
            {ama?.expert_name}
            {ama?.expert_company && ` • ${ama?.expert_company}`}
          </p>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div>
              <Label>{t('ama.form.expertLink')}</Label>
              <div className="flex gap-2 mt-2">
                <Input
                  value={expertLink}
                  readOnly
                  className="font-mono text-sm"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={copyLink}
                >
                  <Copy className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={sendInvitation}
                >
                  <Mail className="mr-2 h-4 w-4" />
                  {t('ama.form.sendInvitation')}
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <div>
        <h2 className="text-2xl font-bold mb-4">{t('ama.moderation.title')}</h2>
        
        <div className="space-y-4">
          {questions?.map((question: AMAQuestion) => (
            <Card key={question.id}>
              <CardContent className="pt-6">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 space-y-2">
                    <div className="flex items-center gap-2">
                      {getStatusBadge(question.status)}
                      {hasAnswer(question.id) && (
                        <Badge variant="outline">{t('ama.moderation.answered')}</Badge>
                      )}
                    </div>
                    <p className="text-sm font-medium">{question.question_text}</p>
                    <div className="text-xs text-muted-foreground">
                      {question.asker_name && `${question.asker_name} • `}
                      {formatDistanceToNow(new Date(question.created_at), {
                        addSuffix: true,
                        locale: nb,
                      })}
                    </div>
                  </div>
                  
                  <div className="flex gap-2">
                    {question.status === 'pending' && (
                      <>
                        <Button
                          size="sm"
                          variant="default"
                          onClick={() => updateStatusMutation.mutate({
                            questionId: question.id,
                            status: 'approved'
                          })}
                        >
                          <Check className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => updateStatusMutation.mutate({
                            questionId: question.id,
                            status: 'rejected'
                          })}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        if (confirm(t('ama.moderation.confirmDelete'))) {
                          deleteQuestionMutation.mutate(question.id);
                        }
                      }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}

          {(!questions || questions.length === 0) && (
            <Card>
              <CardContent className="py-12 text-center">
                <p className="text-muted-foreground">
                  {t('ama.moderation.noQuestions')}
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
      </div>
    </div>
  );
};

export default AMAModeration;
